import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { ConfirmModal } from '../../components/Modal.js';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  reportsApi,
  type ReportColumnMeta,
  type ReportFilterMeta,
  type ReportMeta,
  type ExpApplicantInput,
} from '../../api/reports.js';
import { ApiError } from '../../api/client.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, Field, SearchInput, SortableTh } from '../../components/ui.js';
import { Cell } from '../../components/Cell.js';
import { MemoCell } from '../../components/MemoCell.js';
import { useUrlUpdate, useUrlSearchFields } from '../../lib/hooks.js';
import { money, num } from '../../lib/format.js';

/**
 * 정의 하나로 그려지는 조회 화면.
 *
 * 서버의 /reports/:id/meta 가 필터와 컬럼을 알려주고, 이 컴포넌트가 그대로 그린다.
 * 새 화면을 추가할 때 여기 코드를 고칠 일은 없다 - 서버의 report.registry.ts 에
 * 정의만 하나 늘리면 된다.
 */
export function ReportPage() {
  const { reportId = '' } = useParams();
  return <ReportBody reportId={reportId} />;
}

function koreaToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
}

/**
 * ReportPage 의 본체. reportId 를 URL 이 아니라 prop 으로 받게 뽑아냈다 -
 * randombox-prize-ranking 처럼 정의 기반 리포트 위에 화면 전용 UI(zaiko-stock
 * 의 즐겨찾기 버튼 등)를 얹어야 하는 화면이 이 렌더링을 그대로 재사용하면서
 * headerExtra 자리에 자기 것만 끼워 넣을 수 있게 하려는 것이다.
 *
 * extraColumn 은 표 맨 끝에 정의에 없는 컬럼(zaiko-stock 의 즐겨찾기 1~4번
 * 버튼처럼 쓰기 동작이 필요해 선언적 컬럼으로 못 담는 것)을 하나 더 그린다.
 */
export function ReportBody({
  reportId,
  headerExtra,
  extraColumn,
}: {
  reportId: string;
  headerExtra?: ReactNode;
  extraColumn?: { label: string; render: (row: Record<string, unknown>) => ReactNode };
}) {
  const [params, setParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  // exp-applicants · exp-applicants-option 전용 - 수정 모달에서 지금 고른 행의
  // seq. 다른 리포트에서는 안 쓰인다(버튼 자체가 없다).
  const [editingApplicantSeq, setEditingApplicantSeq] = useState<number | null>(null);
  const isExpApplicants = reportId === 'exp-applicants';
  const isExpApplicantsOption = reportId === 'exp-applicants-option';
  // exp-applicants 전용 - 체크박스로 고른 신청자 seq (원본 selectedItems).
  // 페이지를 넘겨도 유지한다 - 원본은 한 화면에 전부 그렸지만 여기는 100명씩
  // 나눠 그려서, 페이지마다 고른 게 사라지면 100명 넘는 이벤트는 한 번에 처리할
  // 수 없다. 조회 조건(이벤트·상태·검색어)이 바뀌면 비운다 - 안 보이는 다른
  // 이벤트 신청자가 선택에 남아 같이 바뀌는 일을 막는다.
  const [expSelection, setExpSelection] = useState<{ key: string; seqs: ReadonlySet<number> }>({ key: '', seqs: new Set() });

  const meta = useQuery({
    queryKey: ['report', reportId, 'meta'],
    queryFn: () => reportsApi.meta(reportId),
    enabled: Boolean(reportId),
    staleTime: 5 * 60 * 1000,
  });

  const update = useUrlUpdate(params, setParams);

  // 검색어는 입력 중 매 글자마다 URL 을 갱신하지 않도록 늦춘다. 정의마다 검색
  // 필터 개수가 다르므로(원본이 없던 화면도 있다) 여러-칸 버전을 쓴다.
  //
  // 예전에는 검색어를 URL 에 아예 안 썼다 - draft 를 로컬에만 두고 API 호출에만
  // 섞었다. 그래서 dome-price 처럼 '검색어 하나만 있으면 되는' requireFilter
  // 화면은 타이핑을 해도 awaitingSearch(아래, params.get() 을 본다) 가 절대
  // 안 풀렸다 - 실제 결과는 받아놓고도 "검색어를 넣어주세요" 안내만 계속 떴다.
  // URL 로 옮기면 이 문제도 함께 풀린다.
  // 직접 타이핑하는 칸(검색어·= 비교·기간/숫자 범위)은 전부 늦춘다. 드롭다운·
  // 체크박스(options 가 있거나 flag)는 클릭 한 번이 곧 값 하나라 늦출 이유가
  // 없다 - 그건 바로 params 에 반영한다.
  const searchKeys = useMemo(
    () => (meta.data?.filters ?? []).filter((f) => f.type !== 'flag' && !f.options).map((f) => f.key),
    [meta.data],
  );
  // 직접 타이핑하는 칸(검색어·= 비교·기간/숫자 범위)은 URL 에 값이 없으면
  // meta 의 defaultValue 를 그대로 보여준다 - 안 그러면 서버는 이미 그 기본값
  // 으로 조회해 놓고 화면의 입력칸만 빈 채로 보였다 (outbound-work 의 '이번달
  // 25일 기준' 시작일·끝일이 이렇게 안 보이는 채로 있었다).
  //
  // best-order-check 의 soldTo 만 다르다 - 서버 기본값(2099-12-31)은 selectParamFilters
  // 가 "위쪽 없음"을 뜻하려고 쓰는 안전장치 값이라, 그대로 보여주면 사용자에게
  // 이상해 보인다. 화면에는 '오늘'을 대신 보여준다 (아래 queryParams 에서도
  // 실제 조회 조건을 '오늘'로 맞춘다 - 둘이 같은 값이어야 한다).
  const filterDisplayDefaults = useMemo(() => {
    return Object.fromEntries((meta.data?.filters ?? []).map((filter) => [
      filter.key,
      reportId === 'best-order-check' && filter.key === 'soldTo'
        ? koreaToday()
        : filter.defaultValue ?? '',
    ]));
  }, [meta.data, reportId]);
  const [searchValues, setSearchValue] = useUrlSearchFields(params, update, searchKeys, 350, filterDisplayDefaults);

  const queryParams = useMemo(() => {
    const out: Record<string, string | number> = {};
    for (const [k, v] of params.entries()) if (v !== '') out[k] = v;
    // 이 화면의 레거시 SQL 은 카테고리 선택을 전제로 한다. 날짜만 바꿨을 때
    // 전체 카테고리의 비싼 판매량 서브쿼리가 실행되지 않도록 빈 조건으로 둔다.
    if (reportId === 'best-order-check') {
      if (!out.mcode) return {};
      out.soldFrom ??= '2021-01-01';
      out.soldTo ??= koreaToday();
    }
    return out;
  }, [params, reportId]);

  // meta 를 기다리지 않는다. data 요청은 URL 에 있는 값만 쓰고(meta.data 를
  // 안 본다), 서버(runReport)도 requireFilter 를 자기가 직접 판단한다 -
  // meta 를 몰라도 안전하게 빈 결과를 돌려준다. 예전엔 meta 가 끝나야 data 를
  // 시작해서, 화면 하나 열 때마다 원격 왕복 하나만큼 순수하게 더 걸렸다.
  const data = useQuery({
    queryKey: ['report', reportId, queryParams],
    queryFn: () => reportsApi.data(reportId, queryParams),
    enabled: Boolean(reportId),
    placeholderData: keepPreviousData,
  });

  function onSort(key: string, nextDir: 'asc' | 'desc') {
    update({ sort: key, dir: nextDir.toUpperCase() }, false);
  }

  const expSelectionKey = [...params.entries()]
    .filter(([k]) => k !== 'page' && k !== 'sort' && k !== 'dir')
    .map(([k, v]) => `${k}=${v}`).sort().join('&');
  const expSelected = expSelection.key === expSelectionKey ? expSelection.seqs : EMPTY_SEQS;
  function selectExp(seqs: number[], checked: boolean) {
    const next = new Set(expSelected);
    for (const seq of seqs) {
      if (checked) next.add(seq); else next.delete(seq);
    }
    setExpSelection({ key: expSelectionKey, seqs: next });
  }

  if (meta.isPending) return <Spinner />;
  if (meta.isError) return <ErrorBox error={meta.error} onRetry={() => void meta.refetch()} />;

  const visibleColumns = meta.data.columns.filter((c) => !c.hidden);
  // 검색 전에는 서버가 빈 목록을 준다. 데이터가 없는 것과 아직 안 물어본 것을
  // 화면에서 구분해주지 않으면 고장난 것처럼 보인다.
  const awaitingSearch =
    reportId === 'best-order-check'
      ? !(params.get('mcode') ?? '').trim()
      : (meta.data.requireFilter ?? false) &&
        !meta.data.filters.some((f) => (params.get(f.key) ?? '').trim() !== '');
  const sort = params.get('sort') ?? meta.data.defaultSort;
  const dir = (params.get('dir') ?? meta.data.defaultDirection).toUpperCase();

  return (
    // 컬럼이 많은 표는 가운데 정렬된 통(기본 1600px)이 아니라 화면 폭을 전부
    // 쓴다 - 정의가 fullWidth 로 표시한다 (report.types.ts 참고).
    <section
      className={
        isExpApplicants
          ? 'page--full report-exp-page'
          : isExpApplicantsOption
            ? 'page--full report-exp-option-page'
          : reportId === 'best-order-check'
            ? 'report-best-page'
          : reportId === 'partner-landing'
            ? 'page--full partner-landing-page'
          : meta.data.fullWidth
            ? 'page--full'
            : undefined
      }
    >
      <header className={
        isExpApplicantsOption
          ? 'page-head report-exp-option-head'
          : reportId === 'best-order-check'
          ? 'page-head report-best-head'
          : reportId === 'partner-landing'
            ? 'page-head partner-landing-head'
            : 'page-head'
      }>
        <div>
          {isExpApplicantsOption && (
            <span className="report-exp-option-eyebrow">
              <span aria-hidden="true" /> 체험단 운영 · 옵션형
            </span>
          )}
          {reportId === 'partner-landing' && <span className="partner-landing-eyebrow">PARTNER ROUTING</span>}
          <h1 className="page-title">
            {meta.data.title}
            {meta.data.restricted && <span className="badge badge--danger">관리자 전용</span>}
          </h1>
          {meta.data.description && <p className="page-desc">{meta.data.description}</p>}
        </div>
        <div className="page-head__actions">
          {data.isFetching && <span className="page-head__busy" role="status" aria-live="polite">갱신 중…</span>}
          <a
            className={isExpApplicantsOption ? 'btn report-exp-option-export' : 'btn btn--ghost btn--sm'}
            href={reportsApi.exportUrl(reportId, queryParams)}
            // 서버가 attachment 로 내려주므로 새 탭이 아니라 그대로 받는다.
          >
            {isExpApplicantsOption && (
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path d="M10 2.75v9.5m0 0 3.5-3.5m-3.5 3.5-3.5-3.5M4 14.5v1.25A1.25 1.25 0 0 0 5.25 17h9.5A1.25 1.25 0 0 0 16 15.75V14.5" />
              </svg>
            )}
            {isExpApplicantsOption
              ? 'CSV 내보내기'
              : reportId === 'category-excel'
                ? '엑셀 내려받기'
                : 'CSV 내려받기'}
          </a>
        </div>
      </header>

      {headerExtra}

      <div className={
        isExpApplicants
          ? 'filters report-exp-filters'
          : isExpApplicantsOption
            ? 'filters report-exp-option-filters'
          : reportId === 'best-order-check'
            ? 'filters report-best-filters'
            : reportId === 'partner-landing'
              ? 'filters partner-landing-filters'
            : 'filters'
      }>
        {isExpApplicantsOption && (
          <div className="report-exp-option-filters__head">
            <div>
              <span>FILTER</span>
              <strong>신청자 빠르게 찾기</strong>
            </div>
            <p>이벤트를 선택하면 하위 옵션이 자동으로 좁혀집니다.</p>
          </div>
        )}
        {meta.data.filters.filter((f) => !f.hidden).map((f) => (
          <FilterControl
            key={f.key}
            filter={f}
            searchableOptions={reportId === 'best-order-check' && f.key === 'mcode'}
            // dependsOn 이 걸린 선택지는 상위 필터에서 고른 값으로 좁힌다
            // (예: 메인 이벤트를 고르면 그 아래 서브 이벤트만).
            parentValue={f.dependsOn ? (params.get(f.dependsOn) ?? '') : undefined}
            value={params.get(f.key) ?? f.defaultValue ?? ''}
            draft={searchValues[f.key] ?? ''}
            onSearchChange={(v) => setSearchValue(f.key, v)}
            onChange={(v) => update({ [f.key]: v, ...staleDependents(meta.data.filters, params, f.key, v) })}
          />
        ))}
        {reportId === 'exp-applicants' && params.size > 0 && (
          <button type="button" className="btn btn--ghost report-exp-filters__reset" onClick={() => setParams({}, { replace: true })}>
            전체 조건 초기화
          </button>
        )}
        {isExpApplicantsOption && params.size > 0 && (
          <button type="button" className="btn btn--ghost report-exp-option-filters__reset" onClick={() => setParams({}, { replace: true })}>
            조건 초기화
          </button>
        )}
        {reportId === 'best-order-check' && params.size > 0 && (
          <button type="button" className="btn btn--ghost report-best-filters__reset"
            onClick={() => setParams({}, { replace: true })}>조건 초기화</button>
        )}
      </div>

      {awaitingSearch ? (
        reportId === 'best-order-check'
          ? <BestOrderAwaiting />
          : <EmptyBox>검색어를 넣으면 결과를 보여줍니다.</EmptyBox>
      ) : data.isPending ? (
        <Spinner />
      ) : data.isError ? (
        <ErrorBox error={data.error} onRetry={() => void data.refetch()} />
      ) : data.data.items.length === 0 ? (
        <EmptyBox />
      ) : (
        <>
          {reportId === 'exp-applicants' && (
            <ExpApplicantsOverview rows={data.data.items} total={data.data.total} />
          )}
          {isExpApplicantsOption && (
            <ExpApplicantsOptionOverview rows={data.data.items} total={data.data.total} />
          )}
          {reportId === 'best-order-check' && (
            <BestOrderOverview
              rows={data.data.items}
              total={data.data.total}
              totals={data.data.totals}
              mcode={params.get('mcode') ?? ''}
            />
          )}
          {reportId === 'partner-landing' && (
            <PartnerLandingOverview rows={data.data.items} total={data.data.total} />
          )}
          {(() => {
            const split = meta.data.splitBy;
            const rows = data.data.items.map((row, index) => ({ row, index }));
            if (reportId === 'exp-applicants') {
              return (
                <>
                  <ExpApplicantsTable
                    rows={rows}
                    sort={sort}
                    dir={dir}
                    onSort={onSort}
                    page={data.data.page}
                    perPage={data.data.perPage}
                    total={data.data.total}
                    onEdit={(seq) => setEditingApplicantSeq(seq)}
                    selected={expSelected}
                    onSelect={selectExp}
                  />
                  <ExpApplicantsBatchBar
                    selected={expSelected}
                    onClear={() => setExpSelection({ key: expSelectionKey, seqs: new Set() })}
                    onChanged={() => void data.refetch()}
                  />
                </>
              );
            }
            if (isExpApplicantsOption) {
              return (
                <ExpApplicantsOptionTable
                  rows={rows}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                  page={data.data.page}
                  perPage={data.data.perPage}
                  total={data.data.total}
                  onEdit={(seq) => setEditingApplicantSeq(seq)}
                />
              );
            }
            if (reportId === 'best-order-check') {
              return (
                <BestOrderTable
                  rows={rows}
                  columns={visibleColumns}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                  copied={copied}
                  copyError={copyError}
                  onCopy={async () => {
                    const tsv = data.data.items
                      .filter((row) => String(row.isshow ?? '') === 'Y')
                      .map((row) => [row.index_no, String(row.gname ?? '').replace(/<[^>]*>/g, ''), Number(row.sold ?? 0)].join('\t'))
                      .join('\n');
                    try {
                      await copyPlainText(tsv);
                      setCopyError(false);
                      setCopied(true);
                      window.setTimeout(() => setCopied(false), 1800);
                    } catch {
                      setCopied(false);
                      setCopyError(true);
                    }
                  }}
                />
              );
            }
            if (reportId === 'partner-landing') {
              return (
                <PartnerLandingTable
                  rows={rows}
                  columns={meta.data.columns}
                  extraColumn={extraColumn}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                  page={data.data.page}
                  perPage={data.data.perPage}
                />
              );
            }
            if (!split) {
              return (
                <ReportTable
                  rows={rows}
                  columns={visibleColumns}
                  extraColumn={extraColumn}
                  meta={meta.data}
                  reportId={reportId}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                  page={data.data.page}
                  perPage={data.data.perPage}
                  total={data.data.total}
                  totals={data.data.totals}
                  onMemoSaved={() => void data.refetch()}
                />
              );
            }

            // 원본은 진행중/종료를 서버에서 두 배열로 갈라 보냈다. 여기서는 한 번
            // 받은 행을 화면에서 가른다 - 표만 둘일 뿐 같은 조회 결과다.
            //
            // 가른 뒤 번호를 다시 매긴다(index). 원본도 표마다 index+1 로 세서
            // 두 표가 각각 1 부터 시작했다 - 조회 결과 전체 기준으로 세면
            // 진행중 표가 1, 2, 5, 7… 처럼 뛴다(사이사이가 종료 행이라서).
            const isSplit = (r: Record<string, unknown>) => String(r[split.column]) === String(split.value);
            const renumber = (list: typeof rows) => list.map((r, i) => ({ ...r, index: i }));
            const primary = renumber(rows.filter((r) => !isSplit(r.row)));
            const secondary = renumber(rows.filter((r) => isSplit(r.row)));
            const title = (t: string, n: number) => t.replace('{count}', String(n));

            return (
              <>
                <h2 className="report-section__title">{title(split.primaryTitle, primary.length)}</h2>
                <ReportTable
                  rows={primary}
                  columns={visibleColumns}
                  extraColumn={extraColumn}
                  meta={meta.data}
                  reportId={reportId}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                  page={data.data.page}
                  perPage={data.data.perPage}
                  total={data.data.total}
                  totals={sumColumns(primary, split.totalColumns)}
                  totalsLabel={`총 ${primary.length}건 합계`}
                  onMemoSaved={() => void data.refetch()}
                />
                {secondary.length > 0 && (
                  <>
                    <h2 className="report-section__title report-section__title--danger">
                      {title(split.splitTitle, secondary.length)}
                    </h2>
                    <ReportTable
                      rows={secondary}
                      columns={visibleColumns}
                      extraColumn={extraColumn}
                      meta={meta.data}
                      reportId={reportId}
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                      page={data.data.page}
                      perPage={data.data.perPage}
                      total={data.data.total}
                      totals={sumColumns(secondary, split.totalColumns)}
                      totalsLabel={`총 ${secondary.length}건 합계`}
                      tone={split.tone}
                      onMemoSaved={() => void data.refetch()}
                    />
                  </>
                )}
              </>
            );
          })()}

          <Pagination
            page={data.data.page}
            totalPages={data.data.totalPages}
            total={data.data.total}
            onChange={(p) => update({ page: p }, false)}
          />

          {!isExpApplicantsOption && (
            <p className="hint hint--source">원본 화면: <code>{meta.data.legacyPath}</code></p>
          )}

          {(isExpApplicants || isExpApplicantsOption) && editingApplicantSeq !== null && (() => {
            const row = data.data.items.find((r) => Number(r.seq) === editingApplicantSeq);
            // 정렬·페이지를 바꿔 다시 조회하는 사이에 그 행이 안 보일 수 있다
            // (예: 상태가 바뀌어 지금 필터에 더는 안 걸리는 경우). 그때는 그냥
            // 안 그린다 - 빈 폼을 보여줘 봐야 저장할 게 없다.
            if (!row) return null;
            return (
              <ExpApplicantsEditModal
                reportId={isExpApplicantsOption ? 'exp-applicants-option' : 'exp-applicants'}
                seq={editingApplicantSeq}
                row={row}
                onClose={() => setEditingApplicantSeq(null)}
                onSaved={() => {
                  setEditingApplicantSeq(null);
                  void data.refetch();
                }}
              />
            );
          })()}
        </>
      )}
    </section>
  );
}

/**
 * mergeRows 용 - 이어진 같은 값 묶음의 '첫 줄과 그 길이' 를 미리 센다.
 * 반환값 spans[i] 가 0 이면 그 줄은 묶음의 첫 줄이 아니라서 합쳐진 칸을
 * 아예 안 그린다(위 줄의 rowspan 이 대신 덮는다). 0 보다 크면 그만큼 rowSpan.
 *
 * 이어진 행만 본다 - 사용자가 다른 컬럼으로 정렬해 같은 값이 흩어지면 묶음이
 * 잘게 쪼개질 뿐, 떨어져 있는 행이 붙지 않는다. sectionBreaks 에 표시된 줄
 * (sectionBy 의 구분 헤더가 끼어드는 자리)에서도 묶음을 끊는다 - 안 그러면
 * 합쳐진 칸 한가운데를 헤더 행이 가른다.
 */
function mergeSpans(
  rows: { row: Record<string, unknown> }[],
  by: string,
  sectionBreaks: Set<number>,
): number[] {
  const spans = new Array<number>(rows.length).fill(0);
  let start = 0;
  for (let i = 1; i <= rows.length; i++) {
    const sameGroup =
      i < rows.length &&
      !sectionBreaks.has(i) &&
      rows[i]!.row[by] === rows[start]!.row[by];
    if (!sameGroup) {
      spans[start] = i - start;
      start = i;
    }
  }
  return spans;
}

/** 화면에서 더하는 합계 (SQL 로 못 더하는 derive 값이라 여기서 낸다). */
function sumColumns(
  rows: { row: Record<string, unknown> }[],
  keys: string[] | undefined,
): Record<string, number> | undefined {
  if (!keys || keys.length === 0) return undefined;
  const out: Record<string, number> = {};
  for (const key of keys) {
    out[key] = rows.reduce((sum, r) => sum + (Number(r.row[key]) || 0), 0);
  }
  return out;
}

const PARTNER_MATCH_LABELS: Record<string, string> = {
  same: '정확히 일치',
  intext: '포함',
  instart: '으로 시작',
  inend: '으로 끝',
};

function PartnerLandingOverview({
  rows,
  total,
}: {
  rows: Record<string, unknown>[];
  total: number;
}) {
  const active = rows.filter((row) => String(row.isuse ?? '') === 't').length;
  const inactive = rows.length - active;
  const applied = rows.reduce((sum, row) => sum + (Number(row.apply_count) || 0), 0);

  return (
    <section className="partner-landing-overview" aria-label="파트너 랜딩 요약">
      <article className="partner-landing-metric partner-landing-metric--primary">
        <span>전체 조건</span>
        <strong>{num(total)}</strong>
        <small>검색 조건에 해당하는 랜딩 규칙</small>
      </article>
      <article className="partner-landing-metric partner-landing-metric--active">
        <span>현재 페이지 사용</span>
        <strong>{num(active)}</strong>
        <small>유입에 적용되는 활성 조건</small>
      </article>
      <article className={inactive > 0 ? 'partner-landing-metric partner-landing-metric--inactive' : 'partner-landing-metric'}>
        <span>현재 페이지 미사용</span>
        <strong>{num(inactive)}</strong>
        <small>비활성화되어 적용되지 않는 조건</small>
      </article>
      <article className="partner-landing-metric">
        <span>현재 페이지 누적 적용</span>
        <strong>{num(applied)}</strong>
        <small>자동 집계된 적용 횟수 합계</small>
      </article>
    </section>
  );
}

function PartnerLandingTable({
  rows,
  columns,
  extraColumn,
  sort,
  dir,
  onSort,
  page,
  perPage,
}: {
  rows: { row: Record<string, unknown>; index: number }[];
  columns: ReportColumnMeta[];
  extraColumn?: { label: string; render: (row: Record<string, unknown>) => ReactNode };
  sort: string | undefined;
  dir: string;
  onSort: (key: string, nextDir: 'asc' | 'desc') => void;
  page: number;
  perPage: number;
}) {
  const sortable = (label: string, key: string, align?: 'right') => (
    <SortableTh
      label={label}
      sortKey={key}
      currentSort={sort}
      currentDir={dir === 'ASC' ? 'asc' : 'desc'}
      onSort={onSort}
      align={align}
    />
  );
  const dateColumn = (key: string) => columns.find((column) => column.key === key);
  const value = (row: Record<string, unknown>, key: string) => String(row[key] ?? '').trim();

  return (
    <section className="partner-landing-list" aria-label="파트너 랜딩 조건 목록">
      <header className="partner-landing-list__head">
        <div>
          <span>ROUTING RULES</span>
          <h2>랜딩 조건 목록</h2>
        </div>
        <p>조건과 추적값을 한 행에서 확인하고 오른쪽에서 바로 수정할 수 있습니다.</p>
      </header>
      <div className="table-wrap partner-landing-table-wrap">
        <table className="table partner-landing-table">
          <thead>
            <tr>
              {sortable('No', 'seq', 'right')}
              {sortable('파트너 조건', 'partner_id')}
              <th>GA4 추적값</th>
              {sortable('적용 현황', 'apply_count', 'right')}
              <th style={{ textAlign: 'center' }}>사용 상태</th>
              {sortable('최근 변경', 'datemodify')}
              {extraColumn && <th>{extraColumn.label}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, index }) => {
              const active = value(row, 'isuse') === 't';
              const partnerId = value(row, 'partner_id');
              const conditionName = value(row, 'if_name');
              const conditionValue = value(row, 'if_value');
              const match = PARTNER_MATCH_LABELS[value(row, 'if_middle')]
                ?? (value(row, 'if_middle') || '조건 없음');
              const utms = [
                ['source', value(row, '_utm_source')],
                ['medium', value(row, '_utm_medium')],
                ['campaign', value(row, '_utm_campaign')],
              ] as const;
              const modifiedColumn = dateColumn('datemodify');
              const registeredColumn = dateColumn('datereg');

              return (
                <tr key={`${value(row, 'seq')}-${index}`} className={active ? undefined : 'partner-landing-row--inactive'}>
                  <td className="partner-landing-no">{num(Number(row.seq) || (page - 1) * perPage + index + 1)}</td>
                  <td>
                    <div className="partner-landing-condition">
                      <div>
                        <strong>{conditionName || '이름 없는 조건'}</strong>
                        <code>{partnerId || '-'}</code>
                      </div>
                      <span>{match}{conditionValue ? ` · ${conditionValue}` : ''}</span>
                    </div>
                  </td>
                  <td>
                    <div className="partner-landing-utms">
                      {utms.some(([, utm]) => utm) ? utms.map(([label, utm]) => utm && (
                        <span key={label}><small>{label}</small>{utm}</span>
                      )) : <span className="partner-landing-default">기본 추적값 적용</span>}
                    </div>
                  </td>
                  <td className="partner-landing-apply">
                    <strong>{num(Number(row.apply_count) || 0)}</strong>
                    <span>{dateColumn('apply_date') ? <Cell column={dateColumn('apply_date')!} row={row} /> : '-'}</span>
                  </td>
                  <td className="partner-landing-status">
                    <span className={active ? 'partner-landing-status__badge partner-landing-status__badge--active' : 'partner-landing-status__badge partner-landing-status__badge--inactive'}>
                      <i />{active ? '사용' : '미사용'}
                    </span>
                  </td>
                  <td className="partner-landing-dates">
                    <strong>{modifiedColumn ? <Cell column={modifiedColumn} row={row} /> : '-'}</strong>
                    <span>등록 {registeredColumn ? <Cell column={registeredColumn} row={row} /> : '-'}</span>
                  </td>
                  {extraColumn && <td className="partner-landing-actions">{extraColumn.render(row)}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BestOrderAwaiting() {
  return (
    <section className="report-best-awaiting">
      <span aria-hidden="true">⇅</span>
      <div>
        <strong>확인할 카테고리를 선택하세요</strong>
        <p>카테고리를 고르면 추천상품의 진열 순서와 선택한 기간의 실제 판매량을 비교합니다.</p>
      </div>
    </section>
  );
}

async function copyPlainText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('클립보드에 복사하지 못했습니다.');
}

function soldTone(value: unknown): 'high' | 'strong' | 'watch' | 'normal' {
  const sold = Number(value) || 0;
  if (sold >= 1000) return 'high';
  if (sold >= 500) return 'strong';
  if (sold >= 100) return 'watch';
  return 'normal';
}

function BestOrderOverview({
  rows, total, totals, mcode,
}: {
  rows: Record<string, unknown>[];
  total: number;
  totals?: Record<string, number>;
  mcode: string;
}) {
  const soldTotal = Number(totals?.sold ?? rows.reduce((sum, row) => sum + (Number(row.sold) || 0), 0));
  const hidden = rows.filter((row) => String(row.isshow ?? '') !== 'Y').length;
  const soldOut = rows.filter((row) => String(row.isopen ?? '') === '3').length;
  const hot = rows.filter((row) => Number(row.sold ?? 0) >= 500).length;
  return (
    <section className="report-best-overview" aria-label="카테고리 추천상품 요약">
      <article className="report-best-metric report-best-metric--primary">
        <span>추천상품</span><strong>{num(total)}</strong><small>현재 카테고리 등록 상품</small>
      </article>
      {/*
        이 화면은 카테고리의 "추천상품(베스트)" 만 본다. 베스트에 안 든 나머지
        상품은 짝 화면(category-orderby, 상품관리 메뉴의 '상품정렬확인')이 맡는다
        - 레거시 bestitem_itemno_list_utf8.php 는 두 목록을 한 페이지에 이어
        붙였지만, 여기서는 성격이 다른 두 화면(추천상품 대시보드 · 일반상품 표)
        으로 나눴다. 처음 보면 두 화면이 짝이라는 걸 알기 어려워 바로가기를 둔다.
      */}
      {mcode && (
        <Link
          className="report-best-metric report-best-metric--link"
          to={`/reports/category-orderby?catecode=${encodeURIComponent(mcode)}`}
        >
          <span>일반상품(나머지)</span><strong>상품정렬확인 →</strong><small>이 카테고리의 베스트 제외 상품</small>
        </Link>
      )}
      <article className="report-best-metric report-best-metric--sales">
        <span>기간 판매량</span><strong>{num(soldTotal)}</strong><small>선택 기간 누적 판매</small>
      </article>
      <article className="report-best-metric report-best-metric--hot">
        <span>판매 500개 이상</span><strong>{num(hot)}</strong><small>상위 판매 상품</small>
      </article>
      <article className={hidden + soldOut > 0 ? 'report-best-metric report-best-metric--attention' : 'report-best-metric'}>
        <span>상태 확인 필요</span><strong>{num(hidden + soldOut)}</strong><small>진열안함 {hidden} · 품절 {soldOut}</small>
      </article>
    </section>
  );
}

function BestOrderTable({
  rows, columns, sort, dir, onSort, copied, copyError, onCopy,
}: {
  rows: { row: Record<string, unknown>; index: number }[];
  columns: ReportColumnMeta[];
  sort: string | undefined;
  dir: string;
  onSort: (key: string, nextDir: 'asc' | 'desc') => void;
  copied: boolean;
  copyError: boolean;
  onCopy: () => Promise<void>;
}) {
  const imageColumn = columns.find((column) => column.key === 'simg1');
  const nameColumn = columns.find((column) => column.key === 'gname');
  const sortable = (label: string, key: string, align?: 'right') => (
    <SortableTh label={label} sortKey={key} currentSort={sort}
      currentDir={dir === 'ASC' ? 'asc' : 'desc'} onSort={onSort} align={align} />
  );
  return (
    <section className="report-best-list" aria-label="추천상품 진열 순서">
      <header className="report-best-list__head">
        <div><span>DISPLAY ORDER</span><h2>추천상품 진열 순서</h2></div>
        <div className="report-best-list__tools">
          <div className="report-best-legend" aria-label="판매량 색상 기준">
            <span><i className="report-best-dot report-best-dot--high" />1,000개 이상</span>
            <span><i className="report-best-dot report-best-dot--strong" />500개 이상</span>
            <span><i className="report-best-dot report-best-dot--watch" />100개 이상</span>
          </div>
          <button className={copyError ? 'btn btn--ghost btn--sm report-best-copy--error' : 'btn btn--ghost btn--sm'}
            type="button" onClick={() => void onCopy()}>
            {copyError ? '복사 실패 · 다시 시도' : copied ? '복사 완료' : '탭 구분 목록 복사'}
          </button>
        </div>
      </header>
      <div className="table-wrap report-best-table-wrap">
        <table className="table report-best-table">
          <thead><tr>
            {sortable('진열순서', 'orders', 'right')}
            <th>상품</th>
            {sortable('기간판매', 'sold', 'right')}
            <th style={{ textAlign: 'right' }}>판매가</th>
            <th style={{ textAlign: 'center' }}>진열</th>
            <th style={{ textAlign: 'center' }}>판매</th>
          </tr></thead>
          <tbody>
            {rows.map(({ row, index }) => {
              const show = String(row.isshow ?? '') === 'Y';
              const open = String(row.isopen ?? '') === '2';
              const code = String(row.index_no ?? '');
              return (
                <tr key={`${code}-${index}`} className={!show || !open ? 'report-best-row--attention' : undefined}>
                  <td className="report-best-order">{num(row.orders as number)}</td>
                  <td>
                    <div className="report-best-product">
                      {imageColumn && <Cell column={imageColumn} row={row} />}
                      <div>
                        {nameColumn ? <Cell column={nameColumn} row={row} /> : String(row.gname ?? '-')}
                        <small>상품코드 {code}</small>
                      </div>
                    </div>
                  </td>
                  <td className={`report-best-sold report-best-sold--${soldTone(row.sold)}`}>
                    <strong>{num(row.sold as number)}</strong><small>개</small>
                  </td>
                  <td className="report-best-price">{money(row.account as number)}</td>
                  <td className="report-best-status">
                    <span className={show ? 'report-best-badge report-best-badge--ok' : 'report-best-badge report-best-badge--off'}>
                      {show ? '진열' : '진열안함'}
                    </span>
                  </td>
                  <td className="report-best-status">
                    <span className={open ? 'report-best-badge report-best-badge--ok' : 'report-best-badge report-best-badge--soldout'}>
                      {open ? '판매중' : String(row.isopen ?? '') === '3' ? '품절' : `상태 ${String(row.isopen ?? '-')}`}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const EXP_STATUS: Record<string, { label: string; tone: string }> = {
  '1': { label: '신청', tone: 'apply' },
  '2': { label: '당첨(미입금)', tone: 'unpaid' },
  '3': { label: '당첨(입금)', tone: 'paid' },
  '4': { label: '당첨(발송완료)', tone: 'shipped' },
  '5': { label: '당첨(후기완료)', tone: 'reviewed' },
  '6': { label: '취소', tone: 'cancelled' },
  '7': { label: '미당첨', tone: 'rejected' },
  '9': { label: '대기(예약)', tone: 'waiting' },
  '99': { label: '중복신청', tone: 'duplicate' },
  '999': { label: '삭제', tone: 'deleted' },
};

function ExpApplicantsOverview({ rows, total }: { rows: Record<string, unknown>[]; total: number }) {
  const statusCounts = new Map<string, number>();
  for (const row of rows) {
    const status = String(row.status ?? '');
    statusCounts.set(status, (statusCounts.get(status) ?? 0) + 1);
  }
  const count = (...statuses: string[]) => statuses.reduce((sum, status) => sum + (statusCounts.get(status) ?? 0), 0);

  return (
    <section className="report-exp-overview" aria-label="체험단 신청 현황 요약">
      <div className="report-exp-summary">
        <article className="report-exp-summary__item report-exp-summary__item--primary">
          <span>검색 결과</span>
          <strong>{num(total)}</strong>
          <small>현재 조건에 해당하는 전체 신청</small>
        </article>
        <article className="report-exp-summary__item">
          <span>현재 페이지</span>
          <strong>{num(rows.length)}</strong>
          <small>한 화면에 표시된 신청자</small>
        </article>
        <article className="report-exp-summary__item report-exp-summary__item--winner">
          <span>당첨 진행</span>
          <strong>{num(count('2', '3', '4', '5'))}</strong>
          <small>미입금부터 후기완료까지</small>
        </article>
        <article className="report-exp-summary__item report-exp-summary__item--attention">
          <span>확인 필요</span>
          <strong>{num(count('1', '2', '9'))}</strong>
          <small>신청·미입금·예약 상태</small>
        </article>
      </div>
      <div className="report-exp-statuses" aria-label="현재 페이지 상태 분포">
        <span>현재 페이지 상태</span>
        {Object.entries(EXP_STATUS).map(([status, meta]) => {
          const value = statusCounts.get(status) ?? 0;
          return value > 0 ? (
            <span key={status} className={`report-exp-status report-exp-status--${meta.tone}`}>
              {meta.label} <strong>{num(value)}</strong>
            </span>
          ) : null;
        })}
      </div>
    </section>
  );
}

/** 옵션형 체험단은 상태뿐 아니라 어떤 옵션에 신청이 몰렸는지가 핵심이다. */
function ExpApplicantsOptionOverview({ rows, total }: { rows: Record<string, unknown>[]; total: number }) {
  const statusCounts = new Map<string, number>();
  const optionCounts = new Map<string, number>();

  for (const row of rows) {
    const status = String(row.status ?? '');
    const option = String(row.options ?? '').trim() || '옵션 미지정';
    statusCounts.set(status, (statusCounts.get(status) ?? 0) + 1);
    optionCounts.set(option, (optionCounts.get(option) ?? 0) + 1);
  }

  const count = (...statuses: string[]) => statuses.reduce((sum, status) => sum + (statusCounts.get(status) ?? 0), 0);
  const options = [...optionCounts.entries()].sort((a, b) => b[1] - a[1]);
  const visibleOptions = options.slice(0, 8);

  return (
    <section className="report-exp-option-overview" aria-label="옵션 체험단 신청 현황 요약">
      <div className="report-exp-option-metrics">
        <article className="report-exp-option-metric report-exp-option-metric--primary">
          <span>검색 결과</span>
          <strong>{num(total)}</strong>
          <small>현재 조건의 전체 신청자</small>
        </article>
        <article className="report-exp-option-metric">
          <span>현재 페이지</span>
          <strong>{num(rows.length)}</strong>
          <small>지금 화면에 표시된 인원</small>
        </article>
        <article className="report-exp-option-metric report-exp-option-metric--success">
          <span>당첨 진행</span>
          <strong>{num(count('2', '3', '4', '5'))}</strong>
          <small>미입금부터 후기 완료까지</small>
        </article>
        <article className="report-exp-option-metric report-exp-option-metric--warning">
          <span>확인 필요</span>
          <strong>{num(count('1', '2', '9'))}</strong>
          <small>신청 · 미입금 · 예약 상태</small>
        </article>
      </div>

      <div className="report-exp-option-distribution">
        <div className="report-exp-option-distribution__title">
          <div>
            <span>OPTION MIX</span>
            <strong>현재 페이지 옵션 분포</strong>
          </div>
          <small>{num(optionCounts.size)}개 옵션</small>
        </div>
        <div className="report-exp-option-distribution__items">
          {visibleOptions.map(([option, optionCount]) => (
            <div className="report-exp-option-share" key={option}>
              <div>
                <span title={option}>{option}</span>
                <strong>{num(optionCount)}명</strong>
              </div>
              <span className="report-exp-option-share__track" aria-hidden="true">
                <span style={{ width: `${rows.length > 0 ? (optionCount / rows.length) * 100 : 0}%` }} />
              </span>
            </div>
          ))}
          {options.length > visibleOptions.length && (
            <p className="report-exp-option-distribution__more">
              신청이 많은 8개 옵션을 표시했습니다 · 외 {num(options.length - visibleOptions.length)}개
            </p>
          )}
        </div>
      </div>

      <div className="report-exp-option-statuses" aria-label="현재 페이지 상태 분포">
        <span>상태 분포</span>
        {Object.entries(EXP_STATUS).map(([status, meta]) => {
          const statusCount = statusCounts.get(status) ?? 0;
          return statusCount > 0 ? (
            <span key={status} className={`report-exp-status report-exp-status--${meta.tone}`}>
              {meta.label} <strong>{num(statusCount)}</strong>
            </span>
          ) : null;
        })}
      </div>
    </section>
  );
}

function ExpApplicantsOptionTable({
  rows,
  sort,
  dir,
  onSort,
  page,
  perPage,
  total,
  onEdit,
}: {
  rows: { row: Record<string, unknown>; index: number }[];
  sort: string | undefined;
  dir: string;
  onSort: (key: string, nextDir: 'asc' | 'desc') => void;
  page: number;
  perPage: number;
  total: number;
  /** exp-applicants 의 "수정" 버튼과 같다 - 그 행의 seq 를 넘긴다. */
  onEdit: (seq: number) => void;
}) {
  const sortable = (label: string, key: string, align?: 'right') => (
    <SortableTh
      label={label}
      sortKey={key}
      currentSort={sort}
      currentDir={dir === 'ASC' ? 'asc' : 'desc'}
      onSort={onSort}
      align={align}
    />
  );

  return (
    <section className="report-exp-option-list" aria-label="옵션 체험단 신청 리스트">
      <header className="report-exp-option-list__head">
        <div>
          <span>APPLICANTS</span>
          <h2>신청자 목록</h2>
        </div>
        <p><span aria-hidden="true">●</span> 민감한 개인정보가 포함된 관리자 전용 화면입니다.</p>
      </header>

      <div className="table-wrap report-exp-option-table-wrap">
        <table className="table report-exp-option-table">
          <thead>
            <tr>
              {sortable('No', 'seq', 'right')}
              {sortable('이벤트 / 선택 옵션', 'eventitem')}
              {sortable('신청자', 'uname')}
              <th>연락처 / 배송지</th>
              <th>입금 정보</th>
              {sortable('진행 상태', 'status')}
              {sortable('신청 이력', 'regdate')}
              <th>수정</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, index }) => {
              const statusKey = String(row.status ?? '');
              const status = EXP_STATUS[statusKey] ?? { label: statusKey || '알 수 없음', tone: 'unknown' };
              const memberId = String(row.mem_idx ?? '');
              const number = total - ((page - 1) * perPage + index);
              const seq = Number(row.seq);

              return (
                <tr key={String(row.seq ?? index)} className={`report-exp-option-row report-exp-option-row--${status.tone}`}>
                  <td className="td-right report-exp-option-table__no" data-label="No">{num(number)}</td>
                  <td data-label="이벤트 / 선택 옵션">
                    <div className="report-exp-option-event">
                      <strong>{value(row.eventitem)}</strong>
                      <span>{value(row.subevent)}</span>
                      <em>{value(row.options)}</em>
                      {hasValue(row.memo) && <small>{String(row.memo)}</small>}
                    </div>
                  </td>
                  <td data-label="신청자">
                    <div className="report-exp-option-applicant">
                      <span className="report-exp-option-avatar" aria-hidden="true">{String(row.uname ?? '?').trim().charAt(0) || '?'}</span>
                      <div>
                        {memberId ? (
                          <a
                            className="link"
                            href={`https://banana.bizhost.kr/adminx/help.php?code=view&index_no=${encodeURIComponent(memberId)}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {value(row.uname)} <span aria-hidden="true">↗</span>
                          </a>
                        ) : (
                          <strong>{value(row.uname)}</strong>
                        )}
                        <small>{value(row.userid)} · {deviceLabel(row.device)}</small>
                      </div>
                    </div>
                  </td>
                  <td data-label="연락처 / 배송지">
                    <div className="report-exp-option-contact">
                      <strong>{value(row.hphone)}</strong>
                      <span>{hasValue(row.postnew) ? `[${String(row.postnew)}] ` : ''}{value(row.address)}</span>
                    </div>
                  </td>
                  <td data-label="입금 정보">
                    <div className="report-exp-option-deposit">
                      <strong>{value(row.incomename)}</strong>
                      <span>{hasValue(row.incomedate) ? String(row.incomedate) : '입금일 미등록'}</span>
                    </div>
                  </td>
                  <td className="report-exp-option-table__status" data-label="진행 상태">
                    <span className={`report-exp-status report-exp-status--${status.tone}`}>{status.label}</span>
                    {(hasValue(row.gocompany) || hasValue(row.gotablesn)) && (
                      <small>{value(row.gocompany)} · {value(row.gotablesn)}</small>
                    )}
                  </td>
                  <td data-label="신청 이력">
                    <div className="report-exp-option-history">
                      <span><small>신청</small>{value(row.regdate)}</span>
                      <span><small>가입</small>{value(row.signdate)}</span>
                    </div>
                  </td>
                  <td data-label="수정">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={!Number.isFinite(seq)}
                      onClick={() => onEdit(seq)}
                    >
                      수정
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ExpApplicantsTable({
  rows,
  sort,
  dir,
  onSort,
  page,
  perPage,
  total,
  onEdit,
  selected,
  onSelect,
}: {
  rows: { row: Record<string, unknown>; index: number }[];
  sort: string | undefined;
  dir: string;
  onSort: (key: string, nextDir: 'asc' | 'desc') => void;
  page: number;
  perPage: number;
  total: number;
  /** 원본 event/bnn_sextoy_exp_new.php:776 의 "수정" 버튼 - 그 행의 seq 를 넘긴다. */
  onEdit: (seq: number) => void;
  /** 체크박스로 고른 seq (원본 :731 의 selectedItems). */
  selected: ReadonlySet<number>;
  onSelect: (seqs: number[], checked: boolean) => void;
}) {
  // 머리 체크박스는 **이 페이지에 보이는 줄**을 전부 고르거나 푼다(원본 toggleAll).
  const pageSeqs = rows.map(({ row }) => Number(row.seq)).filter((seq) => Number.isFinite(seq));
  const pickedOnPage = pageSeqs.filter((seq) => selected.has(seq)).length;
  const allOnPage = pageSeqs.length > 0 && pickedOnPage === pageSeqs.length;
  const sortable = (label: string, key: string, align?: 'right') => (
    <SortableTh
      label={label}
      sortKey={key}
      currentSort={sort}
      currentDir={dir === 'ASC' ? 'asc' : 'desc'}
      onSort={onSort}
      align={align}
    />
  );

  return (
    <section className="report-exp-list" aria-label="체험단 신청 리스트">
      <header className="report-exp-list__head">
        <div>
          <span>APPLICANTS</span>
          <h2>신청 리스트</h2>
        </div>
        <p>민감한 신청자 정보가 포함된 관리자 전용 화면입니다.</p>
      </header>
      <div className="table-wrap report-exp-table-wrap">
        <table className="table report-exp-table">
          <thead>
            <tr>
              <th className="report-exp-table__check">
                <input
                  type="checkbox"
                  aria-label="이 페이지 전체 선택"
                  checked={allOnPage}
                  ref={(el) => { if (el) el.indeterminate = pickedOnPage > 0 && !allOnPage; }}
                  onChange={(event) => onSelect(pageSeqs, event.target.checked)}
                />
              </th>
              {sortable('No', 'seq', 'right')}
              {sortable('메인 / 서브이벤트', 'eventitem')}
              <th>메모 / 부가정보</th>
              {sortable('신청자 정보', 'uname')}
              <th>연락처 / 주소</th>
              <th>입금 정보</th>
              {sortable('상태', 'status')}
              {sortable('신청 / 가입 이력', 'regdate')}
              <th>수정</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, index }) => {
              const statusKey = String(row.status ?? '');
              const status = EXP_STATUS[statusKey] ?? { label: statusKey || '알 수 없음', tone: 'unknown' };
              const memberId = String(row.mem_idx ?? '');
              const number = total - ((page - 1) * perPage + index);
              const seq = Number(row.seq);
              return (
                <tr
                  key={String(row.seq ?? index)}
                  className={`report-exp-row report-exp-row--${status.tone}${selected.has(seq) ? ' report-exp-row--selected' : ''}`}
                >
                  <td className="report-exp-table__check">
                    <input
                      type="checkbox"
                      aria-label={`${value(row.uname)} (No ${num(number)}) 선택`}
                      disabled={!Number.isFinite(seq)}
                      checked={selected.has(seq)}
                      onChange={(event) => onSelect([seq], event.target.checked)}
                    />
                  </td>
                  <td className="td-right report-exp-table__no">{num(number)}</td>
                  <td>
                    <div className="report-exp-event">
                      <strong>{value(row.eventitem)}</strong>
                      <span>{value(row.subevent)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="report-exp-memo">
                      <p>{value(row.memo)}</p>
                      {hasValue(row.etcinfo1) && <small>{String(row.etcinfo1)}</small>}
                      {hasValue(row.admin_memo) && <em>관리자: {String(row.admin_memo)}</em>}
                    </div>
                  </td>
                  <td>
                    <div className="report-exp-applicant">
                      <span className="report-exp-device" title={deviceLabel(row.device)}>{deviceIcon(row.device)}</span>
                      {memberId ? (
                        <a
                          className="link"
                          href={`https://banana.bizhost.kr/adminx/help.php?code=view&index_no=${encodeURIComponent(memberId)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {value(row.uname)} ↗
                        </a>
                      ) : (
                        <strong>{value(row.uname)}</strong>
                      )}
                      <small>{value(row.userid)}</small>
                    </div>
                  </td>
                  <td>
                    <div className="report-exp-contact">
                      <strong>{value(row.hphone)}</strong>
                      <span>{hasValue(row.postnew) ? `[${String(row.postnew)}] ` : ''}{value(row.address)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="report-exp-deposit">
                      <strong>{value(row.incomename)}</strong>
                      <span>{hasValue(row.incomedate) ? String(row.incomedate) : '입금일 미등록'}</span>
                    </div>
                  </td>
                  <td className="report-exp-table__status">
                    <span className={`report-exp-status report-exp-status--${status.tone}`}>{status.label}</span>
                    {(hasValue(row.gocompany) || hasValue(row.gotablesn)) && (
                      <small>{value(row.gocompany)} · {value(row.gotablesn)}</small>
                    )}
                  </td>
                  <td>
                    <div className="report-exp-history">
                      <span><small>신청</small>{value(row.regdate)}</span>
                      <span><small>가입</small>{value(row.signdate)}</span>
                      {hasValue(row.inaddr) && <em>IP {String(row.inaddr)}</em>}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={!Number.isFinite(seq)}
                      onClick={() => onEdit(seq)}
                    >
                      수정
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const EMPTY_SEQS: ReadonlySet<number> = new Set();

/** 원본 "상태변경 선택" 의 선택지 (bnn_sextoy_exp_new.php:798) - 서버 EXP_BATCH_STATUSES 와 같다. */
const EXP_BATCH_STATUSES = ['2', '3', '4', '5', '6', '7', '99', '999'] as const;

/** 원본 주문서 CSV prompt 의 기본 바코드 (bnn_sextoy_exp_new.php:976). */
const EXP_ORDER_DEFAULT_BARCODE = '2471855';

/** 원본 CNP폼 파일 이름 - sextoyexp_YYYYMMDD + **분**(원본이 date(i) 를 썼다). 확장자만 .xlsx. 서버 expCnpFilename 과 같다. */
function cnpFilename(): string {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', minute: '2-digit',
  }).formatToParts(new Date()).map((p) => [p.type, p.value]));
  return `sextoyexp_${parts.year}${parts.month}${parts.day}${String(parts.minute).padStart(2, '0')}.xlsx`;
}

function koreaStamp(): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(new Date()).replace(/[-:]/g, '').replace(' ', '_');
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 체험단 현황 일괄 처리 막대 - 원본 bnn_sextoy_exp_new.php:788 의 action-footer.
 *
 * 원본에 있던 버튼 중 세 가지만 옮겼다(요청, 2026-11-04):
 *   - 주문서 엑셀: 바코드를 받아 고른 신청자를 비즈호스트 주문서로 내려받는다.
 *     원본은 prompt 창으로 받았다 - 여기서는 버튼 옆 입력칸으로 둔다(기본값은 같다).
 *   - CNP폼 엑셀: 고른 신청자를 택배 접수(CNP) 양식으로 내려받는다(원본 mode=downcsv6).
 * 원본 버튼 이름은 '주문서 CSV'·'CNP폼 CSV' 였다. 파일을 .xlsx 로 바꾸면서(2026-11-04
 * 요청) 이름도 받는 파일에 맞췄다 - 'CSV' 를 누르고 엑셀 파일이 오면 헷갈린다.
 *   - 상태변경 선택 + 적용: 고른 신청자의 상태를 한꺼번에 바꾼다(확인을 한 번 받는다).
 *
 * 표가 길어(기본 100줄) 맨 아래까지 내려가야 버튼이 보이면 불편해서, 고른 게
 * 있을 때 화면 아래에 붙어 따라다닌다.
 */
function ExpApplicantsBatchBar({
  selected,
  onClear,
  onChanged,
}: {
  selected: ReadonlySet<number>;
  onClear: () => void;
  onChanged: () => void;
}) {
  const [barcode, setBarcode] = useState(EXP_ORDER_DEFAULT_BARCODE);
  const [status, setStatus] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  const count = selected.size;
  const seqs = () => [...selected].sort((a, b) => a - b);

  const download = useMutation({
    mutationFn: () => reportsApi.expApplicantOrderExcel(seqs(), barcode.trim()),
    onSuccess: (blob) => {
      saveBlob(blob, `BizHost_${koreaStamp()}.xlsx`);
      setNotice({ tone: 'ok', text: `${num(count)}명의 주문서 엑셀을 내려받았습니다.` });
    },
    onError: (error) => setNotice({ tone: 'error', text: error instanceof Error ? error.message : '내려받지 못했습니다.' }),
  });

  const cnp = useMutation({
    mutationFn: () => reportsApi.expApplicantCnpExcel(seqs()),
    onSuccess: (blob) => {
      saveBlob(blob, cnpFilename());
      setNotice({ tone: 'ok', text: `${num(count)}명의 CNP폼 엑셀을 내려받았습니다.` });
    },
    onError: (error) => setNotice({ tone: 'error', text: error instanceof Error ? error.message : '내려받지 못했습니다.' }),
  });

  const change = useMutation({
    mutationFn: () => reportsApi.batchExpApplicantStatus(seqs(), status),
    onSuccess: (result) => {
      setConfirming(false);
      setNotice({ tone: 'ok', text: `[${num(result.affected)}]개를 수정했습니다.` });
      setStatus('');
      onClear();
      onChanged();
    },
  });

  function startDownload() {
    if (count === 0) return setNotice({ tone: 'warn', text: '선택된 항목이 없습니다.' });
    if (barcode.replace(/[^a-zA-Z0-9-]/g, '') === '') return setNotice({ tone: 'warn', text: '바코드를 입력해야 합니다.' });
    setNotice(null);
    download.mutate();
  }

  function startCnp() {
    if (count === 0) return setNotice({ tone: 'warn', text: '선택된 항목이 없습니다.' });
    setNotice(null);
    cnp.mutate();
  }

  function startChange() {
    if (count === 0) return setNotice({ tone: 'warn', text: '선택된 항목이 없습니다.' });
    if (!status) return setNotice({ tone: 'warn', text: '변경할 상태를 선택하세요.' });
    setNotice(null);
    change.reset();
    setConfirming(true);
  }

  return (
    <div className={`report-exp-batch${count > 0 ? ' report-exp-batch--active' : ''}`} role="region" aria-label="선택한 신청자 일괄 처리">
      <div className="report-exp-batch__count">
        <strong>{num(count)}</strong>명 선택됨
        {count > 0 && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => { onClear(); setNotice(null); }}>선택 해제</button>
        )}
      </div>

      <div className="report-exp-batch__group">
        <label className="report-exp-batch__barcode">
          <span>상품 바코드</span>
          <input
            className="input"
            aria-label="주문서 바코드"
            value={barcode}
            inputMode="numeric"
            onChange={(event) => setBarcode(event.target.value)}
          />
        </label>
        <button type="button" className="btn btn--ghost" onClick={startDownload} disabled={download.isPending}>
          {download.isPending ? '만드는 중…' : '주문서 엑셀'}
        </button>
      </div>

      {/* 바코드를 쓰지 않는다 - 주문서 묶음과 떼어 둔다. */}
      <div className="report-exp-batch__group">
        <button type="button" className="btn btn--ghost" onClick={startCnp} disabled={cnp.isPending}>
          {cnp.isPending ? '만드는 중…' : 'CNP폼 엑셀'}
        </button>
      </div>

      <div className="report-exp-batch__group">
        <select className="input" aria-label="변경할 상태" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">상태변경 선택</option>
          {EXP_BATCH_STATUSES.map((key) => <option key={key} value={key}>{EXP_STATUS[key]!.label}</option>)}
        </select>
        <button type="button" className="btn btn--primary" onClick={startChange} disabled={change.isPending}>적용</button>
      </div>

      {notice && (
        <p className={`report-exp-batch__notice report-exp-batch__notice--${notice.tone}`} role={notice.tone === 'ok' ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}

      {confirming && (
        <ConfirmModal
          title="상태 일괄 변경"
          message={<>선택한 <b>{num(count)}명</b>의 상태를 <b>{EXP_STATUS[status]?.label ?? status}</b>(으)로 변경하시겠습니까?</>}
          confirmLabel="변경"
          danger={status === '999' || status === '6'}
          pending={change.isPending}
          error={change.isError ? (change.error instanceof Error ? change.error.message : '변경하지 못했습니다.') : undefined}
          onConfirm={() => change.mutate()}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

/**
 * 체험단 신청 정보 수정 — 원본 event/bnn_sextoy_exp_edit_new.php.
 *
 * 원본은 목록의 "수정" 버튼이 이 파일을 iframe 모달로 열었다. Vue 앱이 seq 로
 * 조회한 행을 그대로 폼에 채우고, 저장하면 mode=exec 로 UPDATE 했다. 여기서는
 * 이미 표에 있는 값을 그대로 초깃값으로 쓴다 - 목록과 수정 폼이 같은 조회
 * 결과를 보므로 다시 조회할 필요가 없다.
 *
 * 원본이 고치던 칸 그대로다: 이름/수령자·연락처·이메일·입금자·구우편번호·
 * 신우편번호·주소·부가정보·상태·메모(:22-46). 참여 이벤트·입금확인일·신청
 * 정보(신청일·IP·기기)는 원본도 읽기 전용으로 보여주기만 했다.
 *
 * 원본에 있던 히스토리(같은 아이디의 과거 신청 목록, :72-80)는 옮기지 않았다 -
 * 이번 요청은 수정 기능까지였고, 그 표는 편집과 무관한 참고용이었다.
 *
 * exp-applicants-option 도 이 모달을 그대로 쓴다 - 컬럼이 완전히 같고,
 * reportId 만 바뀌어 어느 테이블에 UPDATE 할지·어느 쿼리를 무효화할지를 정한다.
 * 옵션형에서만 있는 "선택 옵션"은 이 화면에서 안 고친다(원본도 읽기 전용
 * 목록이었다) - 그래서 다른 읽기 전용 정보처럼 요약에만 보여준다.
 */
function ExpApplicantsEditModal({
  reportId,
  seq,
  row,
  onClose,
  onSaved,
}: {
  reportId: 'exp-applicants' | 'exp-applicants-option';
  seq: number;
  row: Record<string, unknown>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  const initial: ExpApplicantInput = {
    uname: str(row.uname),
    hphone: str(row.hphone),
    email: str(row.email),
    incomename: str(row.incomename),
    postold: str(row.postold),
    postnew: str(row.postnew),
    address: str(row.address),
    // 원본 <select> 에 없던 값(예: 필터 옵션에만 있는 값)이 와도 그대로 보여준다 -
    // 아래 STATUS_OPTIONS 에 없는 값이면 select 가 빈 채로 뜨는데, 저장을
    // 누르면 목록에 있는 값 중 하나로 다시 골라야 한다(원본에 없던 안전장치).
    status: str(row.status),
    memo: str(row.memo),
    etcinfo1: str(row.etcinfo1),
  };
  const [form, setForm] = useState<ExpApplicantInput>(initial);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const statusMeta = EXP_STATUS[form.status] ?? {
    label: form.status || '상태 없음',
    tone: 'unknown',
  };

  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => reportsApi.updateExpApplicant(reportId, seq, form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report', reportId] });
      onSaved();
    },
  });

  function set<K extends keyof ExpApplicantInput>(key: K, v: ExpApplicantInput[K]) {
    setForm((f) => ({ ...f, [key]: v }));
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel report-exp-edit"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`exp-applicant-title-${seq}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <header className="report-exp-edit__head">
          <div>
            <span>APPLICATION · #{seq}</span>
            <h2 className="modal-title" id={`exp-applicant-title-${seq}`}>신청 정보 수정 (No. {seq})</h2>
            <p>신청자와 이벤트를 확인한 뒤 처리 정보만 수정하세요.</p>
          </div>
          <span className={`report-exp-status report-exp-status--${statusMeta.tone}`}>
            {statusMeta.label}
          </span>
        </header>

        <div className="report-exp-edit__summary" aria-label="신청 요약">
          <div>
            <span>참여 이벤트</span>
            <strong>{value(row.eventitem)}</strong>
            <small>{value(row.subevent)}{hasValue(row.options) ? ` · ${String(row.options)}` : ''}</small>
          </div>
          <div><span>신청자</span><strong>{value(row.uname)}</strong><small>{value(row.userid)}</small></div>
          <div><span>신청일</span><strong>{value(row.regdate)}</strong><small>{deviceLabel(row.device)}</small></div>
          <div>
            <span>입금확인</span>
            <strong>{hasValue(row.incomedate) ? String(row.incomedate) : '미등록'}</strong>
            <small>{value(row.incomename)}</small>
          </div>
        </div>

        <form
          className="report-exp-edit__form"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <aside className="report-exp-edit__context" aria-label="신청자 원본 정보">
            <div className="report-exp-edit__person">
              <span aria-hidden="true">{form.uname.trim().charAt(0) || '?'}</span>
              <div><strong>{form.uname || '이름 없음'}</strong><small>{value(row.userid)}</small></div>
            </div>
            <dl>
              <div><dt>회원번호</dt><dd>{value(row.mem_idx)}</dd></div>
              <div><dt>가입일</dt><dd>{value(row.signdate)}</dd></div>
              <div><dt>신청 기기</dt><dd>{deviceLabel(row.device)}</dd></div>
              <div><dt>신청 IP</dt><dd>{value(row.inaddr)}</dd></div>
            </dl>
            <div className="report-exp-edit__readonly-note">
              <strong>읽기 전용 정보</strong>
              <p>이벤트, 신청일, 입금확인일은 이 수정 화면에서 변경되지 않습니다.</p>
            </div>
          </aside>

          <div className="report-exp-edit__sections">
            <section className="report-exp-edit__section">
              <header><span>01</span><div><h3>신청자 연락처</h3><p>수령자에게 연락할 기본 정보를 확인합니다.</p></div></header>
              <div className="report-exp-edit__grid report-exp-edit__grid--contact">
                <Field label="이름/수령자">
                  <input className="input" aria-label="이름/수령자" value={form.uname}
                    onChange={(e) => set('uname', e.target.value)} />
                </Field>
                <Field label="연락처">
                  <input className="input" type="tel" aria-label="연락처" value={form.hphone}
                    onChange={(e) => set('hphone', e.target.value)} />
                </Field>
                <Field label="이메일" full>
                  <input className="input" type="email" aria-label="이메일" value={form.email}
                    placeholder="example@domain.com" onChange={(e) => set('email', e.target.value)} />
                </Field>
              </div>
            </section>

            <section className="report-exp-edit__section">
              <header><span>02</span><div><h3>배송지</h3><p>경품을 받을 우편번호와 전체 주소입니다.</p></div></header>
              <div className="report-exp-edit__grid report-exp-edit__grid--address">
                <Field label="우편번호">
                  <input className="input" inputMode="numeric" aria-label="우편번호" value={form.postnew}
                    onChange={(e) => set('postnew', e.target.value)} />
                </Field>
                <Field label="주소">
                  <input className="input" aria-label="주소" value={form.address}
                    onChange={(e) => set('address', e.target.value)} />
                </Field>
              </div>
            </section>

            <section className="report-exp-edit__section report-exp-edit__section--processing">
              <header><span>03</span><div><h3>처리 상태</h3><p>당첨 진행 단계와 입금자 정보를 함께 확인합니다.</p></div></header>
              <div className="report-exp-edit__grid report-exp-edit__grid--processing">
                <Field label="현재 상태">
                  <select
                    className={`input report-exp-edit__status-select report-exp-edit__status-select--${statusMeta.tone}`}
                    aria-label="현재 상태"
                    value={form.status}
                    onChange={(e) => set('status', e.target.value)}
                  >
                    {!(form.status in EXP_STATUS) && <option value={form.status}>{form.status || '(없음)'}</option>}
                    {Object.entries(EXP_STATUS).map(([v, s]) => (
                      <option key={v} value={v}>{s.label}</option>
                    ))}
                  </select>
                  <span className="hint">상태 변경 즉시 상단 배지에 반영됩니다.</span>
                </Field>
                <Field label="입금자">
                  <input className="input" aria-label="입금자" value={form.incomename}
                    onChange={(e) => set('incomename', e.target.value)} />
                  <span className="hint">입금확인일 {hasValue(row.incomedate) ? String(row.incomedate) : '미등록'}</span>
                </Field>
                <Field label="부가정보" full>
                  <input className="input" aria-label="부가정보" value={form.etcinfo1}
                    placeholder="옵션, 요청사항 등 추가 정보" onChange={(e) => set('etcinfo1', e.target.value)} />
                </Field>
              </div>
            </section>

            <section className="report-exp-edit__section">
              <header><span>04</span><div><h3>신청 메모</h3><p>지원 동기와 운영 메모를 충분히 확인하고 수정합니다.</p></div></header>
              <Field label="메모">
                <textarea className="input report-exp-edit__memo" aria-label="메모" rows={6}
                  value={form.memo} onChange={(e) => set('memo', e.target.value)} />
              </Field>
              {hasValue(row.admin_memo) && (
                <div className="report-exp-edit__admin-note"><span>관리자 메모</span><p>{String(row.admin_memo)}</p></div>
              )}
            </section>
          </div>

          {save.isError && (
            <div className="qty-msg qty-msg--error report-exp-edit__error" role="alert">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <footer className="report-exp-edit__footer">
            <div>
              <strong>{dirty ? '저장하지 않은 변경사항이 있습니다' : '현재 저장된 값입니다'}</strong>
              <span>저장하면 신청자 정보와 처리 상태가 함께 반영됩니다.</span>
            </div>
            <div>
              <button type="button" className="btn btn--ghost" onClick={onClose}>취소</button>
              <button type="submit" className="btn btn--primary" disabled={save.isPending}>
                {save.isPending ? '저장 중…' : '수정 완료'}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}

function hasValue(raw: unknown) {
  return raw !== null && raw !== undefined && raw !== '';
}

function value(raw: unknown) {
  return hasValue(raw) ? String(raw) : '-';
}

function deviceLabel(raw: unknown) {
  return raw === 'm' ? '모바일' : raw === 'p' ? 'PC' : value(raw);
}

function deviceIcon(raw: unknown) {
  return raw === 'm' ? 'M' : raw === 'p' ? 'PC' : '?';
}

/**
 * 표 하나. splitBy 가 있는 정의는 이 표를 두 번 그린다(진행중 · 종료) -
 * 그래서 헤더/본문/합계를 통째로 여기로 뽑았다.
 *
 * rows 는 {row, index} 다 - 행 번호(rowNumber 컬럼)는 표가 갈려도 조회 결과
 * 전체 기준이어야 해서, 이 표 안의 순서가 아니라 원래 index 로 센다.
 */
function ReportTable({
  rows,
  columns,
  extraColumn,
  meta,
  reportId,
  sort,
  dir,
  onSort,
  page,
  perPage,
  total,
  totals,
  totalsLabel = '합계',
  tone,
  onMemoSaved,
}: {
  rows: { row: Record<string, unknown>; index: number }[];
  columns: ReportColumnMeta[];
  extraColumn?: { label: string; render: (row: Record<string, unknown>) => ReactNode };
  meta: ReportMeta;
  reportId: string;
  sort: string | undefined;
  dir: string;
  onSort: (key: string, nextDir: 'asc' | 'desc') => void;
  page: number;
  perPage: number;
  total: number;
  totals?: Record<string, number>;
  totalsLabel?: string;
  tone?: 'danger';
  onMemoSaved: () => void;
}) {
  const colCount = columns.length + (extraColumn ? 1 : 0);
  // 화면 폭을 다 쓰는 표는 글자·여백까지 줄여야 스무 개 넘는 칸이 다 들어간다.
  const dense = meta.fullWidth ?? false;
  // 합계 행: 원본처럼 앞쪽 컬럼을 한 칸으로 합쳐 "총 N건 합계" 를 오른쪽에
  // 붙이고, 그 뒤부터 컬럼별 합을 적는다.
  const firstTotalIdx = columns.findIndex((c) => totals?.[c.key] !== undefined);

  return (
    <div className={tone === 'danger' ? 'table-wrap table-wrap--danger' : 'table-wrap'}>
      <table className={dense ? 'table table--dense' : 'table'}>
        <thead>
          <tr>
            {columns.map((c) =>
              c.sortable ? (
                <SortableTh
                  key={c.key}
                  label={c.label}
                  sortKey={c.key}
                  currentSort={sort}
                  currentDir={dir === 'ASC' ? 'asc' : 'desc'}
                  onSort={onSort}
                  align={c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : undefined}
                  width={c.width}
                />
              ) : (
                <th key={c.key} style={{ textAlign: c.align ?? 'left', width: c.width }}>
                  {c.label}
                </th>
              ),
            )}
            {extraColumn && <th>{extraColumn.label}</th>}
          </tr>
        </thead>
        <tbody>
          {(() => {
            // 그룹 값이 하나뿐이면(=검색 결과가 사실상 한 사람이면) 섹션을
            // 나눌 이유가 없다 - "여러 개 있을 때만" 나누는 게 목적이다.
            const section = meta.sectionBy;
            const showSections =
              section !== undefined && new Set(rows.map((r) => r.row[section.column])).size > 1;

            // 구분 헤더가 끼어드는 줄 (아래 rowspan 묶음도 여기서 끊긴다).
            const sectionBreaks = new Set<number>();
            if (showSections) {
              for (let i = 1; i < rows.length; i++) {
                if (rows[i]!.row[section!.column] !== rows[i - 1]!.row[section!.column]) {
                  sectionBreaks.add(i);
                }
              }
            }

            // 원본이 rowspan 으로 묶던 칸들 (상품코드·이미지·상품명).
            const merge = meta.mergeRows;
            const spans = merge ? mergeSpans(rows, merge.by, sectionBreaks) : null;
            const mergedColumns = new Set(merge?.columns ?? []);

            let prevSectionValue: unknown;

            return rows.map(({ row, index }, i) => {
              const warn = meta.rowWarn;
              // 원본 zaiko-filter 는 상세 이미지가 하나도 없는 행을 분홍으로
              // 칠해 알렸다 - 값이 비어 있는 것 자체가 신호인 경우다.
              const emptyKey = meta.rowEmptyWarn;
              const emptyValue = emptyKey ? row[emptyKey] : undefined;
              const isEmptyWarnRow =
                emptyKey !== undefined &&
                (Array.isArray(emptyValue)
                  ? emptyValue.length === 0
                  : emptyValue === null || emptyValue === undefined || emptyValue === '');
              // rowWarn 은 "두 값을 비교해 모자란 행" 이라 글자까지 빨갛게
              // 칠하지만, rowEmptyWarn 은 원본처럼 행 배경만 바꾼다.
              const isCompareWarnRow =
                warn !== undefined && Number(row[warn.column]) < Number(row[warn.lessThanColumn]);
              const isWarnRow = isEmptyWarnRow || isCompareWarnRow;

              const sectionStart = showSections && (i === 0 || row[section!.column] !== prevSectionValue);
              if (showSections) prevSectionValue = row[section!.column];

              return (
                <Fragment key={index}>
                  {sectionStart && (
                    <tr className="row--section">
                      <td colSpan={colCount}>
                        {section!.labelColumns
                          .map((lc) => `${lc.label}: ${row[lc.column] ?? '-'}`)
                          .join(' · ')}
                      </td>
                    </tr>
                  )}
                  <tr className={isWarnRow ? 'row--warn' : undefined}>
                    {columns.map((c) => {
                      const warnThisCell = isCompareWarnRow && !warn?.exceptColumns?.includes(c.key);
                      const pk = meta.memoPk ? row[meta.memoPk] : undefined;
                      // 합쳐지는 칸은 묶음의 첫 줄에만 그린다 - 나머지 줄은
                      // 위 줄의 rowSpan 이 덮으므로 <td> 자체를 내지 않는다.
                      const span = spans && mergedColumns.has(c.key) ? spans[i]! : 1;
                      if (span === 0) return null;
                      return (
                        <td
                          key={c.key}
                          rowSpan={span > 1 ? span : undefined}
                          className={
                            [
                              warnThisCell ? 'td-danger' : c.emphasis ? 'td-emphasis' : '',
                              span > 1 ? 'td-merged' : '',
                            ]
                              .filter(Boolean)
                              .join(' ') || undefined
                          }
                          style={{ textAlign: c.align ?? 'left', width: c.width }}
                        >
                          {c.editable && pk !== undefined && pk !== null ? (
                            <MemoCell
                              reportId={reportId}
                              pk={pk as string | number}
                              value={String(row[c.key] ?? '')}
                              onSaved={onMemoSaved}
                            />
                          ) : (
                            <Cell
                              column={c}
                              row={row}
                              totals={totals}
                              rowNumber={
                                c.type === 'rowNumber'
                                  ? c.rowNumberFrom === 'offset'
                                    ? (page - 1) * perPage + index + 1
                                    : total - ((page - 1) * perPage + index)
                                  : undefined
                              }
                            />
                          )}
                        </td>
                      );
                    })}
                    {extraColumn && <td>{extraColumn.render(row)}</td>}
                  </tr>
                </Fragment>
              );
            });
          })()}
        </tbody>
        {totals && (
          <tfoot>
            <tr className="row--total">
              {firstTotalIdx > 0 && (
                <td colSpan={firstTotalIdx} style={{ textAlign: 'right' }}>
                  {totalsLabel}
                </td>
              )}
              {columns.slice(Math.max(firstTotalIdx, 0)).map((c, idx) => (
                <td key={c.key} style={{ textAlign: c.align ?? 'left', width: c.width }}>
                  {totals[c.key] !== undefined
                    ? money(totals[c.key])
                    : firstTotalIdx <= 0 && idx === 0
                      ? totalsLabel
                      : ''}
                </td>
              ))}
              {extraColumn && <td />}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/**
 * dependsOn 이 걸린 필터의 선택지를 상위 값으로 좁힌다.
 *
 * 상위를 아직 안 골랐으면(빈 문자열) 전부 보여준다 - 그때는 좁힐 기준이 없다.
 * 같은 value 가 상위 값 여러 개에 걸쳐 올 수 있어(서브 이벤트 NIPPORI 가
 * CALENDAR2022~2026 에 걸려 있다) value 로 중복을 접는다.
 */
function visibleOptions(
  filter: ReportFilterMeta,
  parentValue: string | undefined,
): { value: string; label: string }[] {
  const all = filter.options ?? [];
  if (!filter.dependsOn || !parentValue) return dedupeByValue(all);
  return dedupeByValue(all.filter((o) => o.parent === parentValue));
}

function dedupeByValue(options: { value: string; label: string }[]): { value: string; label: string }[] {
  const seen = new Set<string>();
  return options.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
}

/**
 * 상위 필터를 바꿨을 때, 그에 딸린 하위 필터의 값이 더는 고를 수 없는 값이면
 * 같이 지운다. 안 지우면 화면의 select 는 비어 보이는데 조회 조건에는 남아
 * 있어서 "왜 결과가 없지" 가 된다.
 */
function staleDependents(
  filters: ReportFilterMeta[],
  params: URLSearchParams,
  changedKey: string,
  nextValue: string,
): Record<string, string> {
  const patch: Record<string, string> = {};
  for (const f of filters) {
    if (f.dependsOn !== changedKey) continue;
    const current = params.get(f.key);
    if (!current) continue;
    const stillValid =
      nextValue === '' || (f.options ?? []).some((o) => o.value === current && o.parent === nextValue);
    if (!stillValid) patch[f.key] = '';
  }
  return patch;
}

function FilterControl({
  filter,
  searchableOptions = false,
  parentValue,
  value,
  draft,
  onChange,
  onSearchChange,
}: {
  filter: ReportFilterMeta;
  searchableOptions?: boolean;
  /** dependsOn 이 걸린 필터에서, 상위 필터가 지금 고른 값. 빈 문자열이면 전체. */
  parentValue?: string;
  value: string;
  draft: string;
  onChange: (v: string) => void;
  onSearchChange: (v: string) => void;
}) {
  if (filter.type === 'search') {
    return (
      <Field label={filter.label}>
        <SearchInput value={draft} placeholder={filter.placeholder ?? ''} onChange={onSearchChange} />
      </Field>
    );
  }

  if (filter.type === 'flag') {
    return (
      <Field label="">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={value === '1'}
            onChange={(e) => onChange(e.target.checked ? '1' : '')}
          />
          {filter.label}
        </label>
      </Field>
    );
  }

  if (filter.options && filter.options.length > 0) {
    if (searchableOptions) {
      return <SearchableFilterSelect filter={filter} value={value} onChange={onChange} />;
    }

    const options = visibleOptions(filter, parentValue);

    // 정의가 이미 '' 값 옵션(예: 품절·예약의 '전체')을 갖고 있으면 여기서 또
    // 안 붙인다 - 안 그러면 '전체' 가 목록에 두 번 뜬다.
    const hasBlankOption = options.some((o) => o.value === '');
    return (
      <Field label={filter.label}>
        <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          {!hasBlankOption && <option value="">전체</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  const inputType =
    filter.type === 'dateFrom' || filter.type === 'dateTo'
      ? 'date'
      : filter.type === 'numFrom' || filter.type === 'numTo'
        ? 'number'
        : 'text';

  // '=' 비교(주문번호 등)·기간/숫자 범위도 검색어와 같이 늦춘다 - 안 그러면
  // 자리수가 긴 값을 타이핑할 때마다(예: 상품번호 7자리) 조회가 매번 걸린다.
  return (
    <Field label={filter.label}>
      <input
        className="input"
        type={inputType}
        value={draft}
        placeholder={filter.placeholder ?? ''}
        onChange={(e) => onSearchChange(e.target.value)}
      />
    </Field>
  );
}

function SearchableFilterSelect({
  filter, value, onChange,
}: {
  filter: ReportFilterMeta;
  value: string;
  onChange: (value: string) => void;
}) {
  const [query, setQuery] = useState('');
  const options = filter.options ?? [];
  const keyword = query.trim().toLocaleLowerCase('ko-KR').replace(/\s+/g, '');
  const visible = keyword === ''
    ? options
    : options.filter((option) =>
      `${option.label} ${option.value}`.toLocaleLowerCase('ko-KR').replace(/\s+/g, '').includes(keyword));
  const visibleValue = visible.some((option) => option.value === value) ? value : '';

  return (
    <Field label={filter.label} as="div">
      <div className="report-best-category">
        <div className="report-best-category__search">
          <span aria-hidden="true">⌕</span>
          <input type="search" value={query} aria-label="카테고리 검색"
            placeholder="카테고리명 또는 코드 검색"
            onChange={(event) => setQuery(event.target.value)} />
          {query && <small>{visible.length}개</small>}
        </div>
        <select className="input" value={visibleValue} aria-label="카테고리 선택"
          onChange={(event) => onChange(event.target.value)}>
          <option value="">{keyword ? `검색 결과 ${visible.length}개` : '카테고리를 선택하세요'}</option>
          {visible.map((option) => (
            <option key={option.value} value={option.value}>{option.label} · {option.value}</option>
          ))}
        </select>
        {keyword && visible.length === 0 && (
          <span className="report-best-category__empty">일치하는 카테고리가 없습니다.</span>
        )}
      </div>
    </Field>
  );
}
